/**
 * Intelligence Studio - Vault/Notebook title generator
 *
 * Goal: produce a human, stable default title for multi-file uploads.
 * - Never depends on "first file only"
 * - Avoids hash-looking names / UUIDs
 * - Adds "(+N more)" hint for multi-file sets
 *
 * NOTE: This is a UI-only suggestion. Users can always edit the title.
 */
function stripExtension(name = '') {
  return String(name).replace(/\.[^/.]+$/, '');
}

function looksLikeRandomId(name = '') {
  const s = String(name).trim();
  if (!s) return true;
  // 20+ contiguous id-ish chars
  if (/^[a-zA-Z0-9_-]{20,}$/.test(s)) return true;
  // mostly numbers
  if (/^\d{6,}$/.test(s)) return true;
  // UUID
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-/i.test(s)) return true;
  // numbers/underscores only
  if (/^[\d_]+$/.test(s)) return true;
  return false;
}

function cleanTitle(s = '') {
  return String(s)
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generate an intelligent vault title from files.
 *
 * @param {File[]} files
 * @returns {string}
 */
export function generateSmartVaultTitle(files) {
  if (!files || files.length === 0) return 'New Vault';

  const meaningfulNames = Array.from(files)
    .map(f => cleanTitle(stripExtension(f?.name || '')))
    .filter(name => name && !looksLikeRandomId(name));

  if (meaningfulNames.length === 0) {
    const date = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `Vault ${date}`;
  }

  let title = meaningfulNames[0];
  title = title.charAt(0).toUpperCase() + title.slice(1);

  if (title.length > 40) {
    title = title.slice(0, 40).trim() + '...';
  }

  if (files.length > 1) {
    title += ` (+${files.length - 1} more)`;
  }

  return title || 'New Vault';
}
